import { useState } from 'react';
import { useAppStore } from '../store';
import { ERROR_CATEGORY_LABELS, ERROR_CATEGORY_COLORS } from '../types';
import type { ErrorCategory } from '../types';
import { PenTool, Star, Filter, AlertTriangle, ChevronRight, BookOpen, FileText, ChevronDown, ChevronUp, PencilLine, Save, X } from 'lucide-react';

export default function AnalysisPage() {
  const { essays, currentEssay, setCurrentEssay, setActiveTab, startPractice, addToFavorites, favorites, categoryFilter, setCategoryFilter, updateErrorContent } = useAppStore();
  const [showOriginal, setShowOriginal] = useState(false);
  const [editingErrorId, setEditingErrorId] = useState<string | null>(null);
  const [draftCorrected, setDraftCorrected] = useState('');
  const [draftExplanation, setDraftExplanation] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const essay = currentEssay || essays[0];

  if (!essay) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle size={48} className="text-slate-300 mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-primary mb-2">还没有分析结果</h2>
          <p className="text-slate-500 mb-6">先上传一篇作文，才能查看错误分析。</p>
          <button onClick={() => setActiveTab('upload')} className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors">
            去上传作文
          </button>
        </div>
      </div>
    );
  }

  const allErrors = essay.errors;
  const filteredErrors = categoryFilter === 'all' ? allErrors : allErrors.filter(e => e.category === categoryFilter);

  // Count errors per category
  const categoryCounts: Partial<Record<ErrorCategory, number>> = {};
  allErrors.forEach(e => {
    categoryCounts[e.category] = (categoryCounts[e.category] || 0) + 1;
  });

  const startEditing = (errorId: string, corrected: string, explanation: string) => {
    setSaveError(null);
    setEditingErrorId(errorId);
    setDraftCorrected(corrected);
    setDraftExplanation(explanation);
  };

  const cancelEditing = () => {
    setSaveError(null);
    setEditingErrorId(null);
    setDraftCorrected('');
    setDraftExplanation('');
  };

  const saveEditing = async () => {
    if (!editingErrorId || !draftCorrected.trim()) return;
    setIsSavingEdit(true);
    setSaveError(null);
    try {
      await updateErrorContent(essay.id, editingErrorId, {
        correctedSentence: draftCorrected.trim(),
        explanationZh: draftExplanation.trim(),
      });
      cancelEditing();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : '保存失败，请稍后重试');
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface py-10 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-primary mb-1">{essay.title}</h1>
            <p className="text-slate-500 text-sm">
              {new Date(essay.uploadedAt).toLocaleDateString('zh-CN')} 上传 · 发现 {allErrors.length} 处错误
            </p>
          </div>
          <button
            onClick={() => startPractice(filteredErrors)}
            disabled={filteredErrors.length === 0}
            className="group inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <PenTool size={18} />
            练习这些错误
            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Essay selector (if multiple) */}
        {essays.length > 1 && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {essays.map(e => (
              <button
                key={e.id}
                onClick={() => setCurrentEssay(e)}
                className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  essay.id === e.id
                    ? 'bg-primary text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {e.title}
              </button>
            ))}
          </div>
        )}

        {/* Original Essay Content */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-8">
          <button
            onClick={() => setShowOriginal(!showOriginal)}
            className="w-full flex items-center justify-between group"
          >
            <h2 className="font-serif text-xl text-primary flex items-center gap-2">
              <FileText size={18} className="text-slate-400" />
              作文原文
            </h2>
            <div className="flex items-center gap-2 text-sm text-slate-400 group-hover:text-primary transition-colors">
              <span>{showOriginal ? '收起' : '展开'}</span>
              {showOriginal ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>
          {showOriginal && (
            <div className="mt-6 p-6 bg-slate-50 rounded-2xl border border-slate-100">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{essay.content}</p>
            </div>
          )}
        </div>

        {/* Error Category Distribution */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-8">
          <h2 className="font-serif text-xl text-primary mb-6 flex items-center gap-2">
            <Filter size={18} className="text-slate-400" />
            错误分布
          </h2>
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={() => setCategoryFilter('all')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                categoryFilter === 'all' ? 'bg-primary text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              全部（{allErrors.length}）
            </button>
            {Object.entries(categoryCounts).map(([cat, count]) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat as ErrorCategory)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                  categoryFilter === cat ? 'text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                style={categoryFilter === cat ? { backgroundColor: ERROR_CATEGORY_COLORS[cat as ErrorCategory] } : {}}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: ERROR_CATEGORY_COLORS[cat as ErrorCategory] }}
                />
                {ERROR_CATEGORY_LABELS[cat as ErrorCategory]} ({count})
              </button>
            ))}
          </div>
        </div>

        {/* Error List */}
        <div className="space-y-4">
          {saveError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {saveError}
            </div>
          )}
          {filteredErrors.map((error, index) => {
            const isFavorited = favorites.some(f => f.error.id === error.id);
            const isEditing = editingErrorId === error.id;
            return (
              <div
                key={error.id}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow duration-300"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold text-white"
                    style={{ backgroundColor: ERROR_CATEGORY_COLORS[error.category] }}
                  >
                    {ERROR_CATEGORY_LABELS[error.category]}
                  </span>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <button
                          onClick={cancelEditing}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                        >
                          <X size={12} />
                          取消
                        </button>
                        <button
                          onClick={saveEditing}
                          disabled={!draftCorrected.trim() || isSavingEdit}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-accent text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          <Save size={12} />
                          {isSavingEdit ? '保存中...' : '保存'}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startEditing(error.id, error.correctedSentence, error.explanationZh || '')}
                        className="p-2 rounded-lg text-slate-300 hover:text-primary hover:bg-slate-100 transition-colors"
                        title="编辑改正句和中文解析"
                      >
                        <PencilLine size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => addToFavorites(error)}
                      className={`p-2 rounded-lg transition-colors ${
                        isFavorited ? 'bg-amber-50 text-amber-500' : 'text-slate-300 hover:text-amber-500 hover:bg-amber-50'
                      }`}
                      title={isFavorited ? '已加入错题集' : '加入错题集'}
                    >
                      <Star size={16} fill={isFavorited ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                </div>

                {/* Original vs Corrected */}
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                    <div className="text-xs font-bold text-red-400 mb-2 tracking-wider">原句（含错误）</div>
                    <p className="text-sm text-red-800 leading-relaxed">{error.originalSentence}</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="text-xs font-bold text-emerald-400 mb-2 tracking-wider">修改后</div>
                    {isEditing ? (
                      <textarea
                        value={draftCorrected}
                        onChange={(e) => setDraftCorrected(e.target.value)}
                        rows={3}
                        className="w-full text-sm text-emerald-900 leading-relaxed bg-white/90 border border-emerald-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300"
                      />
                    ) : (
                      <p className="text-sm text-emerald-800 leading-relaxed">{error.correctedSentence}</p>
                    )}
                  </div>
                </div>

                {/* Explanation */}
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="text-xs font-bold text-slate-400 mb-2 tracking-wider flex items-center gap-1">
                    <BookOpen size={12} />
                    语法解析
                  </div>
                  {isEditing ? (
                    <textarea
                      value={draftExplanation}
                      onChange={(e) => setDraftExplanation(e.target.value)}
                      rows={4}
                      className="w-full text-sm text-slate-700 leading-relaxed bg-white border border-slate-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-slate-300"
                    />
                  ) : (
                    <>
                      <p className="text-sm text-slate-600 leading-relaxed">{error.explanationZh || '暂无中文解析，可点击右上角编辑补充。'}</p>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
